// No store, http, or session access.
//
// Split out of auth.helper.js so that session-globals can derive role booleans
// without importing a module that reaches back into the Pinia store graph.

import get from 'lodash/get';
import USER_ROLES from '@/constants/user-roles';
import { SUBSCRIPTION_ADMIN_ROLE } from '@/settings/user-management/utils/role-ids.constants';

export function hasGlobalAccess (user) {
    // Check if exists and is non-zero; BE won't send if already expired
    return !!user.globalAccessExpiresAt;
}

export function isReadOnly (user) {
    return get(user, 'flags.readOnly', false);
}

export function isUser (user) {
    if (isReadOnly(user)) {
        return false;
    }

    return user.role === USER_ROLES.USER;
}

export function isAdmin (user) {
    return get(user, 'flags.admin', false);
}

export function isV2SubAdmin (user) {
    return !!user.subscriptionRoles?.includes(SUBSCRIPTION_ADMIN_ROLE);
}

export function isSuper (user) {
    return user.role === USER_ROLES.SUPER;
}

export function isSupport (user) {
    return get(user, 'userflags.supportFlag', false);
}

export function isGuest (user) {
    return !!user.expiresAt;
}

export function isOps (user) {
    return isSuper(user) && get(user, 'userflags.opsFlag', false);
}

export function isPendozer (user) {
    return get(user, 'isPendozer', false);
}

export function isAdoptManager (user) {
    return (isAdmin(user) && get(user, 'flags.adoptManager', false)) || isOps(user);
}

export function hasAdminAccess (user) {
    if (isAdmin(user)) {
        return true;
    }

    return user.role >= USER_ROLES.ADMIN;
}

// Mirrors the backend authorization for the /serviceaccount API group (see
// server/api/api.go). With roles & permissions v2 enabled the backend requires
// a v2 subscription admin or an ops super; otherwise (legacy) it allows a strict
// admin or any super. Keeping this in lockstep prevents the FE from letting a
// user attempt an action the BE will reject with a 403.
//
// The v2 branch also accepts isAdmin because isV2SubAdmin is currently inert: it
// reads user.subscriptionRoles, which /api/init does not send (see APP-163681).
// The v1 admin flag is persisted from the v2 subscription roles server-side, so
// it identifies v2 subscription admins while that helper is repaired. Note this
// must stay isAdmin (strict flags.admin) and not hasAdminAccess, which would also
// admit non-ops supers that the backend 403s.
export function canManageServiceAccounts (user, hasRolesAndPermissionsV2) {
    if (hasRolesAndPermissionsV2) {
        return isV2SubAdmin(user) || isOps(user) || isAdmin(user);
    }

    return isAdmin(user) || isSuper(user);
}

export function roleName (user) {
    if (isGuest(user)) {
        return 'Guest';
    }

    if (isOps(user)) {
        return 'Ops';
    }

    if (isSuper(user)) {
        return 'Super';
    }

    if (isAdmin(user)) {
        return 'Admin';
    }

    if (isUser(user)) {
        return 'User';
    }

    return 'Read-Only';
}

// Mutates a user object to account for the change in the way that we
// promote `USER` to `ADMIN` post APP-2428, setting user.flags.admin
// whether user.role is USER_ROLES.ADMIN
export function convertAdminRoleToFlag (user) {
    if (isSuper(user)) {
        return user;
    }

    if (!user.flags) {
        user.flags = {};
    }
    user.flags.admin = user.role === USER_ROLES.ADMIN;

    // The `POST .../user` call will error if `role === User.Roles.ADMIN`
    if (get(user, 'flags.admin', false)) {
        user.role = USER_ROLES.USER;
    }

    return user;
}

//  Mutates a user object to account for the change in the way that we
//  promote `USER` to `ADMIN` pre APP-2428, making user.role match the
//  constants in USER_ROLES.
export function convertAdminFlagToRole (user) {
    if (isSuper(user)) {
        return user;
    }

    if (get(user, 'flags.admin', false)) {
        user.role = USER_ROLES.ADMIN;
    }

    return user;
}
